import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useApp } from '@/contexts/AppContext';
import { User } from '@/types/user';
import { Button } from '@/components/ui/button';
import FormField from './FormField';
import SelectField from './SelectField';
import { healthcareTitles, healthcareDepartments } from '@/types/profileTypes';
import { useCurrentUser, useUpdateUser } from '@/hooks/useCurrentUser';
import { useCurrentUserModule, useCompleteModule } from '@/hooks/useUserModule';
import { useNoGuardNavigate, useNoGuard } from '@/hooks/useNoGuard';
import { ResponsiveContainer } from './ResponsiveContainer';

interface ProfileFormProps {
  formData: User;
  setFormData: React.Dispatch<React.SetStateAction<User>>;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

const ProfileForm = ({ formData, setFormData, errors, setErrors }: ProfileFormProps) => {
  const regularNavigate = useNavigate();
  const noGuardNavigate = useNoGuardNavigate();
  const isNoGuard = useNoGuard();
  const navigate = isNoGuard ? noGuardNavigate : regularNavigate;
  const { dispatch } = useApp();
  const { data: user, isLoading, isFetching, isError } = useCurrentUser();
  const { mutate: updateUser } = useUpdateUser();
  const { data: currentModule } = useCurrentUserModule();
  const { mutate: completeModule } = useCompleteModule();
  
  useEffect(() => {
    if (user) {
      setFormData(user);
    }
  }, [user, setFormData]);

  // Handle navigation in useEffect to avoid render-time side effects
  useEffect(() => {
    if (isError || (!isLoading && !user)) {
      navigate('/login');
    }
  }, [isError, isLoading, user, navigate]);

  if (isError || !user) {
    return null; // Prevent rendering if user data is not available
  }
  if (!formData || typeof formData.firstName !== 'string') return <div>Loading form data...</div>;

  if (isLoading || isFetching) return <div>Loading...</div>;
  if (isError) return <div>Error loading user data</div>;

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.title) {
      newErrors.title = 'Job title is required';
    } else if (formData.title === 'Other' && !formData.customTitle?.trim()) {
      newErrors.customTitle = 'Please specify your job title';
    }

    if (!formData.department) {
      newErrors.department = 'Department is required';
    } else if (formData.department === 'other' && !formData.customDepartment.trim()) {
      newErrors.customDepartment = 'Please specify your department';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Update user profile
    updateUser(formData, {
      onSuccess: () => {
        // Complete the profile module if we're on it
        if (currentModule?.module?.screenType === 'profile') {
          completeModule(currentModule.id, {
            onSuccess: () => {
              // Navigate to the next module after completion
              navigate('/module');
            },
            onError: () => {
              // Navigate anyway if module completion fails
              navigate('/module');
            }
          });
        } else {
          // Navigate directly if not on profile module
          navigate('/module');
        }
      }
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <ResponsiveContainer maxWidth="2xl">
      <form onSubmit={handleSubmit} className="space-y-6 py-6 lg:py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          <FormField
            id="firstName"
            label="First Name"
            value={formData.firstName}
            onChange={(value) => handleInputChange('firstName', value)}
            error={errors.firstName}
            placeholder="First name"
            autoComplete="given-name"
            autoFocus
            required
          />

          <FormField
            id="lastName"
            label="Last Name"
            value={formData.lastName}
            onChange={(value) => handleInputChange('lastName', value)}
            error={errors.lastName}
            placeholder="Last name"
            autoComplete="family-name"
            required
          />
        </div>

        <FormField
          id="nickname"
          label="Preferred Name"
          value={formData.nickname}
          onChange={(value) => handleInputChange('nickname', value)}
          placeholder="How would you like to be addressed?"
          helpText="This is how we'll address you in conversations"
        />

        <SelectField
          id="title"
          label="Job Title"
          value={formData.title}
          onChange={(value) => handleInputChange('title', value)}
          error={errors.title}
          placeholder="Select your job title"
          options={healthcareTitles}
          showCustomInput={formData.title === 'Other'}
          customValue={formData.customTitle}
          onCustomChange={(value) => handleInputChange('customTitle', value)}
          customError={errors.customTitle}
          customPlaceholder="Please specify your job title"
        />

        <SelectField
          id="department"
          label="Department"
          value={formData.department}
          onChange={(value) => handleInputChange('department', value)}
          error={errors.department}
          placeholder="Select your department"
          options={healthcareDepartments}
          showCustomInput={formData.department === 'other'}
          customValue={formData.customDepartment}
          onCustomChange={(value) => handleInputChange('customDepartment', value)}
          customError={errors.customDepartment}
          customPlaceholder="Please specify your department"
        />

        <Button
          type="submit"
          className="w-full md:max-w-xs bg-black text-white hover:bg-[#666666] h-12 lg:h-14 rounded-lg text-base lg:text-lg font-medium transition-colors"
        >
          Complete registration
        </Button>
      </form>
    </ResponsiveContainer>
  );
};

export default ProfileForm;
